import { useNavigate } from "react-router-dom";
import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { setJobDetail } from "@store/actions/job.action";
import { deleteJob, getJobs, updateJob } from "@endpoints/job.service";
import { EnumJobStatus2, JobStatus } from "@utils/constants";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import PlusIcon from "@assets/svg/PlusIcon";
import { NavLink } from "react-router-dom";
import { SearchIcon } from "lucide-react";
import LoadingAnimation from "@assets/svg/LoadingAnimation";

export function getStatusColors({ status }: { status: EnumJobStatus2 }) {
  switch (status) {
    case EnumJobStatus2.LIVE:
      return "border-status-live";
    case EnumJobStatus2.EXPIRED:
      return "border-status-expired";
    default:
      return "border-black";
  }
}

export function getStatusTextColors({ status }: { status: EnumJobStatus2 }) {
  switch (status) {
    case EnumJobStatus2.LIVE:
      return "text-status-live";
    case EnumJobStatus2.EXPIRED:
      return "text-status-expired";
    default:
      return "text-black";
  }
}

export const JobCard = ({ data }) => {
  const dispatch = useDispatch();
  const isClosed = data?.status === JobStatus.CLOSED && "Closed";
  const isExpired = data?.status === JobStatus.EXPIRED && "Expired";
  const highlightCount = data?.totalApplicants && !isExpired && !isClosed;
  const applicantStatus = [
    {
      label: "New",
      value: data?.newApplicants,
    },
    {
      label: "Read",
      value: data?.viewedApplicants,
    },
    {
      label: "Rejected",
      value: data?.rejectedApplicants,
    },
    {
      label: "All",
      value: data?.totalApplicants,
    },
  ];

  return (
    <NavLink
      to={`/job/${data?._id}/job-details`}
      state={data}
      onClick={() => {
        dispatch(setJobDetail(data));
        // navigate(`/job/${data?._id}/applicants`, { state: data });
      }}
      className="grid grid-cols-2 py-5 border-t hover:border-y hover:bg-gray-100"
    >
      <div className="flex flex-col pl-9 pr-5 pt-5 border-l-4 border-line-accent">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xss font-semibold text-content-muted tracking-wider">
              Candidate views
            </span>
            <span className="text-xss font-semibold text-content">
              {data?.jobViews}
            </span>
          </div>
          <div className="rounded-full py-0.5 px-2 bg-gray-100 text-light-black text-xss font-semibold">
            {data?.status === JobStatus.LIVE && "Live"}
            {data?.status === JobStatus.DRAFT && "Draft"}
            {data?.status === JobStatus.UNPUBLISHED && "Unpublished"}
            {data?.status === JobStatus.CLOSED && "Closed"}
            {data?.status === JobStatus.EXPIRED && "Expired"}
          </div>
        </div>
        <div className="flex flex-col gap-1 mt-2">
          <span className="text-lg font-semibold text-content">
            {data?.title}
          </span>
          <span className="text-xss font-semibold text-content-muted tracking-wider">
            {data?.refNo}
          </span>
          <div className="flex items-center gap-3">
            <span className="text-xss font-medium text-content-faint">
              Posted by
            </span>
            <span className="text-xss font-semibold text-content-secondary">
              N/A
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xss font-medium text-content-faint">
              Hiring company
            </span>
            <span className="text-xss font-semibold text-content-secondary">
              {data?.hiringCompany?.companyName || "N/A"}
            </span>
          </div>
        </div>
        <div className="flex items-center justify-between border-t pt-2 mt-4">
          <div className="flex flex-col flex-1">
            <span className="text-xss font-semibold text-content-faint">
              Published
            </span>
            <span className="text-xss font-semibold text-content-muted">
              {" "}
              {data?.publishedDate &&
                dayjs(data?.publishedDate).format("hh:mm a Do MMM YYYY")}
            </span>
          </div>
          <div className="flex flex-col flex-1">
            <span className="text-xss font-semibold text-content-faint">
              Closed
            </span>
            <span className="text-xss font-semibold text-content-muted">
              {isClosed
                ? dayjs(isClosed ? data?.updatedAt : data?.expiredAt).format(
                    "hh:mm a Do MMM YYYY",
                  )
                : "_"}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-col border-l px-5">
        <div className="flex items-center gap-6 border-b py-3.5">
          <span className="text-xs font-semibold text-content">Applicants</span>
          <div className="flex items-center flex-1">
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xss text-content-faint font-medium">
                First
              </span>

              <span className="text-xss text-content-muted font-semibold flex-1">
                {data?.firstApplicantAt ? (
                  <>
                    {dayjs(data?.firstApplicantAt).format("hh:mm a")}
                    &nbsp;•&nbsp;
                    {dayjs(data?.firstApplicantAt).format("DD MMM YYYY")}
                  </>
                ) : (
                  "---"
                )}
              </span>
            </div>
            <div className="flex items-center gap-2 flex-1">
              <span className="text-xss text-content-faint font-medium">
                Latest
              </span>

              <span className="text-xss text-content-muted font-semibold flex-1">
                {data?.firstApplicantAt ? (
                  <>
                    {dayjs(data?.firstApplicantAt).format("hh:mm a")}
                    &nbsp;•&nbsp;
                    {dayjs(data?.firstApplicantAt).format("DD MMM YYYY")}
                  </>
                ) : (
                  "---"
                )}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-4 py-3.5">
          {applicantStatus?.map((status) => {
            return (
              <div
                key={status?.label}
                className="flex flex-col items-center rounded-lg border-dashed border-line-strong border text-xss font-semibold py-1 min-w-14 bg-surface-subtle"
              >
                <span className="text-content-faint">{status?.label}</span>
                <span className="text-content-faint">{status?.value || 0}</span>
              </div>
            );
          })}
          <div className="flex flex-col items-center flex-1 border-y-0 border-l-4 border-l-line-strong bg-surface-subtle py-1 h-10">
            <span className="text-xss font-semibold text-content-faint">
              Progressing
            </span>
            <span className="text-xss font-semibold">
              {data?.progressingApplicants || 0}
            </span>
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xss font-semibold text-content">
              Latest Activity
            </span>
            <div className="flex items-center gap-1 text-xsss font-medium text-content-subtle">
              {data?.latestTrackerDetails?.createdAt ? (
                <>
                  <span>
                    {dayjs(data?.latestTrackerDetails?.createdAt).format(
                      "hh:mm a",
                    )}
                  </span>
                  •
                  <span>
                    {dayjs(data?.latestTrackerDetails?.createdAt).format(
                      "Do MMM YYYY",
                    )}
                  </span>
                </>
              ) : null}
            </div>
          </div>
          <div className="flex items-center border-l-4 border-y py-2.5 italic pl-4 text-xss font-medium text-content-subtle border-line-strong border-y-line">
            {data?.latestTrackerDetails?.status || "N/A"}
          </div>
        </div>
      </div>
    </NavLink>
  );
};

export const jobStatusFilters = [
  {
    label: "Active",
    value: EnumJobStatus2.LIVE,
  },
  {
    label: "Draft",
    value: EnumJobStatus2.DRAFT,
  },
  {
    label: "Expired",
    value: EnumJobStatus2.EXPIRED,
  },
  {
    label: "All",
    value: "All",
  },
];

const LIMIT = 15;

const ViewByJob = () => {
  const navigate = useNavigate();
  const scrollListRef = useRef(null);
  const sentinelRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(false);
  const [activeFilter, setActiveFilter] = useState<any>("All");
  const [searchText, setSearchText] = useState("");

  const [jobs, setJobs] = useState([]);

  const fetchJobs = useCallback(async () => {
    try {
      if (page > 1) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      const { success, data, pagination } = await getJobs({
        limit: LIMIT,
        page,
        jobStatus: typeof activeFilter === "string" ? undefined : activeFilter,
      });
      if (success) {
        setJobs((prev) => {
          return page === 1 ? data : [...prev, ...data];
        });
        setHasMoreData(pagination?.hasMore);
      }
    } catch (error) {
      enqueueSnackbar(error, { variant: "error" });
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [page, activeFilter, searchText]);

  function handleLoadMore() {
    if (!hasMoreData || isLoadingMore || isLoading) return;
    setPage((prev) => prev + 1);
  }

  const handleCloseJob = async (jobId, status) => {
    try {
      const { success } = await updateJob({ jobId, status });
      if (success) {
        fetchJobs();
        enqueueSnackbar("Job Closed Successfully!", { variant: "success" });
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  useEffect(() => {
    if (!scrollListRef?.current || !sentinelRef?.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries?.[0]?.isIntersecting) {
          handleLoadMore();
        }
      },
      {
        root: scrollListRef?.current,
        rootMargin: "0px",
        threshold: 1,
      },
    );
    observer.observe(sentinelRef?.current);
    return () => observer.disconnect();
  }, [isLoading, isLoadingMore]);

  // useEffect(() => {
  //   if(jobs?.length){
  //     jobs?.map((item)=>{
  //       deleteJob(item?._id)
  //     })
  //   }
  // }, [jobs])

  return (
    <div className="flex flex-col min-h-0 flex-1 w-full">
      {/* Filter Section */}
      <div className="flex items-center justify-between py-3.5 px-4">
        {/* Search container */}
        <div className="flex items-center w-max border rounded-md bg-gray-50 px-3 gap-2">
          <SearchIcon color="gray" size={16} />
          <input
            className="w-64 text-xs font-medium bg-transparent outline-none h-9"
            placeholder="Search job title, reference or company"
          />
        </div>
        {/* Filters container */}
        <div className="flex items-center bg-surface-muted rounded-md h-9 p-1">
          {jobStatusFilters?.map((filter) => {
            const isSelected = filter?.value === activeFilter;
            return (
              <button
                key={filter?.value}
                onClick={() => setActiveFilter(filter?.value)}
                className={`flex items-center gap-2 h-full px-2.5 ${isSelected ? "bg-white rounded-md h-full text-charcoal" : "text-content-subtle"}`}
              >
                <span className="font-semibold text-xss">{filter?.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Table Header */}
      <div className="flex items-center justify-between gap-5 border-y px-4 py-3 bg-surface-subtle text-content-subtle">
        <span className="uppercase flex-1 text-xss font-semibold tracking-wider">
          Job Post
        </span>
        <div className="h-3 w-px bg-light-grey" />
        <span className="uppercase flex-1 text-xss font-semibold tracking-wider">
          Applications
        </span>
      </div>
      {/* List section */}
      <div className="flex flex-col min-h-0 flex-1 overflow-y-scroll no-scrollbar">
        {isLoading ? (
          <div className="gap-2.5 flex flex-col mt-3">
            {Array.from({ length: 10 }, (x, i) => i).map((key) => {
              return (
                <div key={key} className="flex justify-between">
                  <div
                    style={{
                      borderWidth: 1,
                    }}
                    className="flex rounded-lg w-full pl-3 pr-8 py-4 gap-1.5"
                  >
                    <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                    <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                    <div className="h-3 rounded bg-gray-200 w-1/4"></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : !jobs?.length ? (
          <div className="flex flex-1 h-screen flex-col items-center justify-center gap-2">
            <div className="flex flex-col items-center gap-0.5">
              <span className="text-charcoal font-semibold text-xs">
                No Job created
              </span>
              <span className="text-xs font-medium text-light-black">
                Create a Job now!
              </span>
            </div>
            <button
              onClick={() => navigate("/job/create")}
              className="flex items-center justify-center border bg-charcoal rounded-md h-10 gap-1 text-white px-4 text-xs font-semibold"
            >
              <PlusIcon className={"w-4 h-4"} fill={"white"} />
              Create Job Post
            </button>
          </div>
        ) : (
          <>
            <div
              ref={scrollListRef}
              className="flex flex-col overflow-y-scroll no-scrollbar"
            >
              {jobs?.map((job, jobIndex) => {
                return <JobCard key={job?._id} data={job} />;
              })}
              <div ref={sentinelRef} className="h-px"></div>
            </div>
            {isLoadingMore ? (
              <div className="flex items-center mx-auto text-xs font-medium text-light-black italic gap-2">
                <LoadingAnimation color="black" />
                Loading more data...
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ViewByJob;
